import { Component } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { SplitByBusinessUnitModel, SplitTimingModel, EpicSplitClient } from '../../../client/ApiClient';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Toolbar from '../../basic/Toolbar';
import { DataTable, DataRow, DataCell } from 'components/basic/DataTable';

interface EpicSplitByBusinessUnitDetailProps extends RouteComponentProps<{ id: string }> { }

interface EpicSplitByBusinessUnitDetailState {
    split: SplitByBusinessUnitModel | null;
    timings: SplitTimingModel[] | null;
    lastSequenceId: number | null;
    loading: boolean;
    timingsLoading: boolean;
    error: string | null;
    timingsError: string | null;
}

class EpicSplitByBusinessUnitDetail extends Component<EpicSplitByBusinessUnitDetailProps, EpicSplitByBusinessUnitDetailState> {
    private client: EpicSplitClient = new EpicSplitClient();
    private timingsPageSize = 50;
    private timingsFetchTimeout: NodeJS.Timeout | null = null;

    constructor(props: EpicSplitByBusinessUnitDetailProps) {
        super(props);
        this.state = {
            split: null,
            timings: [],
            lastSequenceId: null,
            loading: true,
            timingsLoading: true,
            error: null,
            timingsError: null
        };
    }

    componentDidMount() {
        const id = this.props.match.params.id;
        this.client.get(id)
            .then(splitResult => {
                this.setState({ split: splitResult, loading: false });
            })
            .catch(err => {
                this.setState({ error: 'Failed to load details.', loading: false });
            });
        this.fetchTimings();
    }

    componentWillUnmount() {
        if (this.timingsFetchTimeout) {
            clearTimeout(this.timingsFetchTimeout);
        }
    }

    fetchTimings() {
        if (this.timingsFetchTimeout) {
            clearTimeout(this.timingsFetchTimeout);
        }
        const timings = this.state?.timings;
        const lastSequenceId = this.state?.lastSequenceId;
        const id = this.props.match.params.id;
        var timeout = 5000;
        console.log('Fetching timings for id:', id, 'with lastSequenceId:', lastSequenceId);
        try {
            this.client.getTimings(id, lastSequenceId ?? null, this.timingsPageSize).then(timingsResult => {
                const resultArray = timingsResult ?? [];
                const merged = timings ? [...timings, ...resultArray] : resultArray;
                const newLastSequenceId = merged.length > 0 ? merged[merged.length - 1].sequenceId : null;
                this.setState({ timings: merged, timingsLoading: false, lastSequenceId: newLastSequenceId })
                if (resultArray.length === this.timingsPageSize) {
                    timeout = 500;
                }
                console.log('Setting timout for id:', id, 'with lastSequenceId:', lastSequenceId, 'with timeout:', timeout);
                this.timingsFetchTimeout = setTimeout(() => this.fetchTimings(), timeout);
            });
        } catch (err) {
            this.setState({ timingsError: 'Failed to load timings.', timingsLoading: false });
        }
    }

    isNullOrEmpty(value: string | null | undefined): boolean {
        return value === null || value === undefined || value.toString().trim() === '';
    }

    handleBackToList = () => {
        this.props.history.push('/tools/split/epic');
    }

    formatMilliseconds(ms?: number | null): string {
        if (ms == null || ms < 0) return '';
        const totalSeconds = Math.floor(ms / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');
        if (days >= 1) {
            return `${days}.${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        } else {
            return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        }
    }

    formatElapsedTime(date: Date | undefined) {
        if (date == undefined) {
            return '';
        }
        const now = new Date();
        let ms = now.getTime() - date.getTime();
        return this.formatMilliseconds(ms);
    }


    getSortedTimings(): SplitTimingModel[] {
        const { timings } = this.state;
        if (!timings) return [];
        // Sort by sequenceId descending
        return [...timings].sort((a, b) => b.sequenceId - a.sequenceId);
    }

    render() {
        const { split, loading, timingsLoading, error, timingsError } = this.state;
        const sortedTimings = this.getSortedTimings();

        return (
            <Container className="mt-4">
                <Row className="mb-2">
                    <Col xs={8} md={9}><h4>Epic Split By Business Unit Details</h4></Col>
                    <Col xs={4} md={3}><Button color='secondary' className='submit-button-orange' onClick={this.handleBackToList}>Back to List</Button></Col>
                </Row>
                {loading ? (
                    <Row><Col>Loading details...</Col></Row>
                ) : error ? (
                    <Row><Col className="text-danger">{error}</Col></Row>
                ) : (
                    <>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Original Database:</strong></Col>
                            <Col xs={8} md={9}>{split?.originalDatabaseName}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Filtered Database:</strong></Col>
                            <Col xs={8} md={9}>{split?.filteredDatabaseName}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Agency List:</strong></Col>
                            <Col xs={8} md={9}>{this.isNullOrEmpty(split?.agencyList) ? "N/A" : split?.agencyList}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Branch List:</strong></Col>
                            <Col xs={8} md={9}>{this.isNullOrEmpty(split?.branchList) ? "N/A" : split?.branchList}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Department List:</strong></Col>
                            <Col xs={8} md={9}>{this.isNullOrEmpty(split?.departmentList) ? "N/A" : split?.departmentList}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Status:</strong></Col>
                            <Col xs={8} md={9}>{split?.status}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Error Message:</strong></Col>
                            <Col xs={8} md={9}>{this.isNullOrEmpty(split?.errorMessage) ? "N/A" : split?.errorMessage}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Started:</strong></Col>
                            <Col xs={8} md={9}>{split?.startTime ? new Date(split?.startTime).toLocaleString() : ''}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={4} md={3}><strong>Finished:</strong></Col>
                            <Col xs={8} md={9}>{split?.endTime ? new Date(split?.endTime).toLocaleString() : ''}</Col>
                        </Row>
                        {/* Timings Table */}
                        <Row className="mt-4">
                            <Col xs={12}>
                                <h5>Epic Split Timings</h5>
                                {timingsError ? (
                                    <div className="text-danger">{timingsError}</div>
                                ) : (
                                    <>
                                        <DataTable>
                                            <thead>
                                                <tr>
                                                    <th>Action</th>
                                                    <th>Table</th>
                                                    <th>Execution Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedTimings && sortedTimings.length > 0 ? sortedTimings.map(timing => (
                                                    <DataRow id={timing.id} key={timing.id}>
                                                        <DataCell>{timing.action}</DataCell>
                                                        <DataCell>{timing.tableName}</DataCell>
                                                        <DataCell>{timing.executionTimeInMilliseconds ? this.formatMilliseconds(timing.executionTimeInMilliseconds) : this.formatElapsedTime(timing.startedOn)}</DataCell>
                                                    </DataRow>
                                                )) : (
                                                    <tr><td colSpan={7} className="text-center">No timings available.</td></tr>
                                                )}
                                            </tbody>
                                        </DataTable>
                                        {timingsLoading && (
                                            <div>Loading more timings...</div>
                                        )}
                                    </>
                                )}
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        );
    }
}

export default withRouter(EpicSplitByBusinessUnitDetail);